import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { Loader2, Lock, Shield, ShieldCheck, TriangleAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/authBootstrap';
import { finishPasswordSetup } from '@/lib/passwordSetup';
import { dbg } from '@/lib/dbg';

const MIN_PASSWORD_LENGTH = 8;

type PasswordStage = 'checking-session' | 'requires-password' | 'completed' | 'no-session' | 'error';

export default function SetPassword() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [stage, setStage] = useState<PasswordStage>('checking-session');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifySession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        dbg.warn('[SET_PASSWORD] Supabase session missing');
        setStage('no-session');
        toast({
          title: 'Invitation session expired',
          description: 'Please reopen your invitation email and follow the link again.',
          variant: 'destructive',
        });
        return;
      }

      // Session exists, allow password setup
      dbg.log('[SET_PASSWORD] Valid session found, showing password form', {
        userEmail: session.user?.email
      });
      setStage('requires-password');
    };

    verifySession();
  }, [toast]);

  const passwordsMatch = useMemo(() => password === confirmPassword, [password, confirmPassword]);
  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || stage !== 'requires-password') return;

    setIsSubmitting(true);
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
      setIsSubmitting(false);
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (!passwordSaved) {
        const { error: updateError } = await supabase.auth.updateUser({
          password, data: { requirePasswordChange: false }
        });
        if (updateError) throw new Error(updateError.message);
        setPasswordSaved(true);
      }
      const destination = await finishPasswordSetup();

      // Get user email before signing out (with fallback)
      let userEmail = '';
      try {
        const { data: { user } } = await supabase.auth.getUser();
        userEmail = user?.email || '';
      } catch (getUserError) {
        dbg.warn('[SET_PASSWORD] Could not get user email, but continuing', getUserError);
      }

      // Sign out to force login with new credentials (helps password managers)
      try {
        await supabase.auth.signOut();
        dbg.log('[SET_PASSWORD] Successfully signed out user');
      } catch (signOutError) {
        dbg.warn('[SET_PASSWORD] Sign out failed, but continuing', signOutError);
      }

      toast({
        title: 'Password set successfully',
        description: destination === '/auth/mobile-ready' ? 'Open the ReActor mobile app and sign in with your new password.' : 'Please sign in with your email and new password.',
      });
      setStage('completed');

      // Force full page reload to login (ensures auth state is fully cleared)
      const loginUrl = destination === '/auth/mobile-ready' ? destination : userEmail ? `/login?email=${encodeURIComponent(userEmail)}` : '/login';
      setTimeout(() => {
        window.location.href = loginUrl;
      }, 2000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(message);
      toast({
        title: 'Unable to set password',
        description: message,
        variant: 'destructive',
      });
      setStage('requires-password');
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, password, passwordSaved, passwordsMatch, passwordTooShort, setLocation, stage, toast]);

  if (stage === 'checking-session') {
    return (
      <PageContainer>
        <StatusCard
          icon={Loader2}
          title="Preparing account setup…"
          description="Hold on while we confirm your invitation."
          loading
        />
      </PageContainer>
    );
  }

  if (stage === 'no-session') {
    return (
      <PageContainer>
        <StatusCard
          icon={TriangleAlert}
          title="Invitation session expired"
          description="Please reopen the invite email and follow the link again."
          variant="warning"
        >
          <Button onClick={async () => { await supabase.auth.signOut(); setLocation('/login'); }}>Back to login</Button>
        </StatusCard>
      </PageContainer>
    );
  }

  if (stage === 'completed') {
    return (
      <PageContainer>
        <StatusCard
          icon={ShieldCheck}
          title="Password successfully set"
          description="Your password has been saved. You'll be redirected to the login page to sign in."
          variant="success"
        >
          <Button onClick={async () => { await supabase.auth.signOut(); setLocation('/login'); }}>Go to login</Button>
        </StatusCard>
      </PageContainer>
    );
  }

  if (stage === 'error') {
    return (
      <PageContainer>
        <StatusCard
          icon={TriangleAlert}
          title="Something went wrong"
          description="We couldn’t finish setting up your account. Please retry from the invitation email."
          variant="destructive"
        >
          <Button variant="outline" onClick={async () => { await supabase.auth.signOut(); setLocation('/login'); }}>Back to login</Button>
        </StatusCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1 text-center">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <Shield className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">ReActor</h1>
            </div>
          </div>
          <CardTitle className="text-2xl">Set your password</CardTitle>
          <CardDescription>Choose a password to finish activating your account.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(event) => { setPassword(event.target.value); setPasswordSaved(false); }}
                placeholder="Enter a secure password"
                autoComplete="new-password"
                required
              />
              <p className="text-xs text-gray-500">Must be at least {MIN_PASSWORD_LENGTH} characters long.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Re-enter your password"
                autoComplete="new-password"
                required
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {!passwordsMatch && confirmPassword.length > 0 && (
              <Alert variant="warning">
                <AlertDescription>Passwords do not match.</AlertDescription>
              </Alert>
            )}
            {passwordTooShort && (
              <Alert variant="warning">
                <AlertDescription>
                  Password must be at least {MIN_PASSWORD_LENGTH} characters long.
                </AlertDescription>
              </Alert>
            )}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving password…
                </>
              ) : (
                <>
                  <Lock className="mr-2 h-4 w-4" />
                  Save password
                </>
              )}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={async () => { await supabase.auth.signOut(); setLocation('/login'); }}>
              Go to login
            </Button>
          </form>
        </CardContent>
      </Card>
    </PageContainer>
  );
}

function PageContainer({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      {children}
    </div>
  );
}

interface StatusCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  variant?: 'default' | 'warning' | 'destructive' | 'success';
  loading?: boolean;
  children?: React.ReactNode;
}

function StatusCard({ icon: Icon, title, description, variant = 'default', loading, children }: StatusCardProps) {
  const iconClass = useMemo(() => {
    if (loading) return 'text-blue-600';
    switch (variant) {
      case 'warning':
        return 'text-amber-500';
      case 'destructive':
        return 'text-red-600';
      case 'success':
        return 'text-green-600';
      default:
        return 'text-blue-600';
    }
  }, [variant, loading]);

  return (
    <Card className="max-w-md w-full">
      <CardHeader className="text-center space-y-2">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-blue-100">
          <Icon className={`h-6 w-6 ${loading ? 'animate-spin' : ''} ${iconClass}`} />
        </div>
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      {children && (
        <CardContent className="flex flex-col gap-3 text-center">
          {children}
        </CardContent>
      )}
    </Card>
  );
}
